/**
 * Watch Providers Modal Component
 * Manages the provider watch list with search and filter functionality
 * Issue #77 - Medical Director Dashboard Implementation
 */

import { useState, useEffect, useMemo } from 'react';
import { 
  Modal, 
  Row, 
  Col, 
  Select,
  Input,
  Button, 
  Typography, 
  List,
  Card
} from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

interface Facility {
  id: number;
  name: string;
}

interface Provider {
  id: number;
  name: string;
  is_watched: boolean;
  assigned_to: Array<{ facility_id: number }>;
}

interface WatchProvidersModalProps {
  open: boolean;
  onClose: () => void;
  providers: Provider[];
  facilities: Facility[];
  onToggleWatch: (providerId: number) => void;
}

function WatchProvidersModal({
  open,
  onClose,
  providers,
  facilities,
  onToggleWatch,
}: WatchProvidersModalProps) {
  const [selectedFacility, setSelectedFacility] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [localProviders, setLocalProviders] = useState<Provider[]>(providers);

  // Sync local providers with parent providers prop
  useEffect(() => {
    setLocalProviders(providers);
  }, [providers]);


  // Handle local watch toggle
  const handleLocalToggleWatch = (providerId: number) => {
    setLocalProviders(prevProviders =>
      prevProviders.map(provider =>
        provider.id === providerId
          ? { ...provider, is_watched: !provider.is_watched }
          : provider
      )
    );
    // Also call the parent handler
    onToggleWatch(providerId);
  };

  // Filter providers based on facility selection and search text
  const filteredProviders = useMemo(() => {
    return localProviders.filter(provider => {
      // Facility filter
      if (selectedFacility !== null) {
        const isAssignedToFacility = provider.assigned_to.some(
          assignment => assignment.facility_id === selectedFacility
        );
        if (!isAssignedToFacility) return false;
      }

      // Search filter
      if (searchText && !provider.name.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [localProviders, selectedFacility, searchText]);

  // Separate watched and available providers
  const watchedProviders = filteredProviders.filter(p => p.is_watched);
  const availableProviders = filteredProviders.filter(p => !p.is_watched);

  const handleClose = () => {
    // Reset filters when closing
    setSelectedFacility(null);
    setSearchText('');
    onClose();
  };

  return (
    <Modal
      title="Watch Providers"
      open={open}
      onCancel={handleClose}
      width={800}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>
      ]}
    >
      {/* Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12}>
          <Text strong>Filter by Facility:</Text>
          <Select
            style={{ width: '100%', marginTop: '4px' }}
            placeholder="All Facilities"
            allowClear
            value={selectedFacility}
            onChange={setSelectedFacility}
          >
            {facilities.map(facility => (
              <Option key={facility.id} value={facility.id}>
                {facility.name}
              </Option>
            ))}
          </Select>
        </Col>
        
        <Col xs={24} sm={12}>
          <Text strong>Search by Name:</Text>
          <Input
            style={{ marginTop: '4px' }}
            placeholder="Search providers..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
      </Row>

      {/* Provider Lists */}
      <Row gutter={[16, 16]}>
        {/* Available Providers */}
        <Col xs={24} sm={12}>
          <Card 
            title={`Available Providers (${availableProviders.length})`}
            size="small"
            style={{ height: '400px' }}
          >
            <div style={{ height: '340px', overflowY: 'auto' }}>
              <List
                dataSource={availableProviders}
                renderItem={(provider) => (
                  <List.Item
                    actions={[
                      <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleLocalToggleWatch(provider.id)}
                      >
                        Watch
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={provider.name}
                    />
                  </List.Item>
                )}
              />
            </div>
          </Card>
        </Col>

        {/* Watched Providers */}
        <Col xs={24} sm={12}>
          <Card 
            title={`Watched Providers (${watchedProviders.length})`}
            size="small"
            style={{ height: '400px' }}
          >
            <div style={{ height: '340px', overflowY: 'auto' }}>
              <List
                dataSource={watchedProviders}
                renderItem={(provider) => (
                  <List.Item
                    actions={[
                      <Button
                        type="default"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleLocalToggleWatch(provider.id)}
                      >
                        Unwatch
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={provider.name}
                    />
                  </List.Item>
                )}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Summary */}
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <Text type="secondary">
          Showing {filteredProviders.length} providers • {watchedProviders.length} watched • {availableProviders.length} available
        </Text>
      </div>
    </Modal>
  );
}

export default WatchProvidersModal;